/* eslint-disable no-shadow */
import { ContentHighlights, ContentMembership } from '@optimus/models';

export interface ContentFeedSection {
  index: number;
  name: string;
}
export interface ContentHeroFormat {
  ext: string;
  url: string;
  hash: string;
  mime: string;
  name: string;
  path: string | null;
  size: number;
  width: number;
  height: number;
}
export interface ContentExternalLink {
  id: number,
  target: string
}

export interface ContentLikeStatus {
  like: boolean;
  dislike: boolean;
  save: boolean;
}

export interface ContentStatus {
  [key: string]: ContentLikeStatus;
}

export interface PodcastDestination {
  platform: string,
  url: string,
}

export interface ContentPodcast {
  destinations: PodcastDestination[],
}

export interface MetaItem {
  id: string;
  name: string;
  category: string;
}

export interface Content {
  id: number,
  attributes: {
    content_collection: {
      data: {
        attributes: {
          title: string | null,
          icon: {
            data: {
              attributes: {
                url: string,
              }
            }
          }
        }
      }
    },
    content_owner: {
      data: {
        attributes: {
          display_name: string | null,
          icon: {
            data: {
              attributes: {
                url: string,
              }
            }
          }
        }
      }
    },
    short_description: string,
    playbook_goal?: string | null,
    title: string,
    tags: string[],
    createdAt: string,
    updatedAt: string,
    publishedAt: string,
    published_date?: string,
    source_published_date?: string,
    type: string,
    video_url: string | null,
    scrap_url: string | null,
    body: string | null,
    highlights?: ContentHighlights | null,
    form_id: string | null,
    hero?: {
      data?: {
        id: number,
        attributes: {
          name: number,
          alternativeText: string | null,
          caption: string | null,
          width: number,
          height: number,
          formats: {
            thumbnail: ContentHeroFormat,
            medium: ContentHeroFormat,
            small: ContentHeroFormat
          },
          hash: string,
          ext: string,
          mime: string,
          size: number,
          url: string,
          previewUrl: string | null,
          provider: string,
          provider_metadata: string | null,
          createdAt: string,
          updatedAt: string
        }
      }
    },
    banner?: {
      id: number,
      short_description: string | null,
    },
    external_link: ContentExternalLink,
    podcast?: ContentPodcast,
    metadata?: MetaItem[],
    locked?: boolean,
    lowestMembershipRequired?: ContentMembership,
    author?: string | null,
  },
  likeStatus?: ContentLikeStatus,
  // optional frontend specific prop for the content feed section the content belongs
  contentFeedSection?: ContentFeedSection,
  // optional frontend specific prop for the content's position
  // in it's content feed section. position starts from 1
  contentFeedSectionPosition?: number,
}

export enum ContentInteractionAction {
  Like = 'Like',
  Dislike = 'Dislike',
  RemoveLike = 'RemoveLike',
  RemoveDislike = 'RemoveDislike',
  Share = 'Share',
  Save = 'Save',
  Unsave = 'Unsave',
  Play = 'Play',
  Open = 'Open',
  Listen = 'Listen',
  View = 'View',
  Pause = 'Pause',
  RecastVote = 'RecastVote',
  Respond = 'Respond'
}

export enum FeedInteractionAction {
  Visit = 'Visit',
  ReachEnd = 'ReachEnd',
  Click = 'Click',
  Filter = 'Filter',
}

export enum SearchInteractionAction {
  Start = 'Start',
  Submit = 'Submit',
  ReachEnd = 'ReachResultsEnd',
}

export enum ContentShareDestination {
  Facebook = 'facebook',
  Twitter = 'twitter',
  WhatsApp = 'whatsApp',
  Linkedin = 'linkedin',
  Copy = 'copy',
  Email = 'email'
}

export const enum ContentFeedFilterType {
  All = 'All',
  Featured = 'Featured',
  New = 'New',
  Marketing = 'Marketing',
  Operations = 'Operations',
  Leadership = 'Leadership',
  Sales = 'Sales',
  Financial = 'Financial',
}

export type PollInteractionAction = ContentInteractionAction.RecastVote |
ContentInteractionAction.Respond

export enum PodcastChannelName {
  web = 'Web',
  spotify = 'Spotify',
  apple = 'Apple Podcasts',
  google = 'Google Podcasts',
  rss = 'RSS Feed',
}
